import React, { useState, useEffect } from 'react'
import { Box, IconButton, Tooltip, getPaletteColor } from 'pcln-design-system'
import styled, { keyframes, css } from 'styled-components'
import HeartIcon from './Heart'

const burst = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.5);
    opacity: 0.8;
  }
  50% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0;
  }
`

const pop = keyframes`
  0% {
    transform: scale(1);
  }
  70% {
    transform: scale(1);
  }
  80% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
`
const burstAfter = keyframes`
  0% { transform: translate(-50%, -50%) rotate(45deg) scale(0.5); opacity: 0.8; }
  50% { transform: translate(-50%, -50%) rotate(45deg) scale(1); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) rotate(45deg) scale(1.1); opacity: 0; }
`

const heartFill = keyframes`
  100% { color: ${getPaletteColor('promoSecondary.base')}; }
`

const heartBeat = keyframes`
  0%, 100% { transform: scale(0.95); }
  30% { transform: scale(0.6); }
  40% { transform: scale(0.7); }
  50% { transform: scale(0.8); }
  70% { transform: scale(0.9); }
`


const AnimatedHeartContainer = styled(Box)`
  cursor: pointer;
  position: relative;

  .heart-svg {
    width: 32px;
    position: relative;
    animation: ${({ animate }) =>
      animate
        ? css`${pop} 0.35s ease-in-out, ${heartFill} 0.25s linear forwards`
        : 'none'};
  }

  .heart {
    color: ${({ isFavorite }) =>
      isFavorite ? getPaletteColor('promoSecondary.base') : getPaletteColor('background.darkest')};
    opacity: ${({ isFavorite }) => (isFavorite ? 1 : 0.5)};
    stroke-width: 2px;
    stroke: getPaletteColor('background.lightest');
    transition: 0.25s ease-in-out;

    &:hover {
      color: ${({ isFavorite }) => (isFavorite ? '' : getPaletteColor('background.darkest'))};
      opacity: 1;
      stroke: ${getPaletteColor('promoSecondary.darkest')};
      animation: ${heartBeat} 1.5s infinite ease-in;
    }
  }

  .heart-burst, .heart-burst::after {
    position: absolute;
    width: 5px;
    height: 5px;
    background-color: transparent;
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0);
    top: 50%;
    left: 50%;
    box-shadow:
      0px -24px 0 ${getPaletteColor('promoSecondary.base')},
      0px 24px 0 ${getPaletteColor('promoSecondary.base')},
      -24px -1px 0 ${getPaletteColor('promoSecondary.base')},
      24px -1px 0 ${getPaletteColor('promoSecondary.base')};
  }

  .heart-burst {
    animation: ${({ animate }) =>
      animate ? css`${burst} 0.5s ease-in-out forwards 0.2s` : 'none'};
  }

  .heart-burst::after {
    content: '';
    animation: ${({ animate }) =>
      animate ? css`${burstAfter} 0.5s ease-in-out forwards 0.2s` : 'none'};
  }
`

const AnimatedHeart = ({ isFavorite, shouldAnimate }) => {
  return (
    <AnimatedHeartContainer isFavorite={isFavorite} animate={shouldAnimate}>
      <HeartIcon className="heart-svg heart" />
      {shouldAnimate && <span className="heart-burst" />}
    </AnimatedHeartContainer>
  )
}


const FavoriteWrapper = styled(Box)`
  position: absolute;
  top: 4px;
  left: 4px;
  -webkit-tap-highlight-color: transparent;
`

const TooltipWrapper = styled(Box)`
  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const FavoriteToggle = ({ initialFavoriteState, onFavoriteToggle }) => {
  const [isFavorite, setIsFavorite] = useState(initialFavoriteState)
  const [isTooltipOpen, setTooltipOpen] = useState(false)
  const [wasToggled, setWasToggled] = useState(false)

  useEffect(() => {
    setIsFavorite(initialFavoriteState)
  }, [initialFavoriteState])

  const handleFavoriteClick = () => {
    const newFavoriteState = !isFavorite
    setIsFavorite(newFavoriteState)
    if (onFavoriteToggle) {
      onFavoriteToggle(newFavoriteState)
    }
    if (newFavoriteState) {
      setWasToggled(true);
      setTimeout(() => setWasToggled(false), 600)
    }
  }

  const showTooltip = () => setTooltipOpen(true)
  const hideTooltip = () => setTooltipOpen(false)

  return (
    <FavoriteWrapper>
      <IconButton
        icon={<AnimatedHeart isFavorite={isFavorite} shouldAnimate={wasToggled}/>}
        aria-label={isFavorite ? 'Remove from your favorites' : 'Save to your favorites'}
        onClick={handleFavoriteClick}
        size='small'
        bg='background.lightest'
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      />
      {isTooltipOpen && (
        <TooltipWrapper>
          <Tooltip bottom center bg='background.darkest' color='text.lightest'>
            <Box width='103px'>
              {isFavorite ? 'Remove from your favorites' : 'Save to your favorites'}
            </Box>
          </Tooltip>
        </TooltipWrapper>
      )}
    </FavoriteWrapper>
  )
}

export default FavoriteToggle
